import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Award, Users, Target, TrendingUp, BookOpen, Shield, Calendar, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import { ISOProgressionPath } from '../components/training/ISOProgressionPath';
import { RelatedCoursesSection } from '../components/training/RelatedCoursesSection';
import { standards } from '../data/standards/standardsData';

export const ISO22000LeadAuditor = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "ISO 22000 Lead Auditor Course | Food Safety Management System Certification",
    "description": "Professional ISO 22000 Lead Auditor Course for food safety auditors. Globally recognized FSMS lead auditor certification covering HACCP, PRP auditing, and food chain compliance.",
    "provider": {
      "@type": "Organization",
      "name": "ISDE Academy",
      "sameAs": "https://www.isde.academy"
    },
    "coursePrerequisites": "Food safety or internal auditing experience recommended",
    "educationalLevel": "Advanced",
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": ["In-person", "Online"],
      "duration": "5 days"
    }
  };

  const courseCompetencies = [
    'Lead FSMS audit teams across the food chain',
    'Evaluate food safety hazards, HACCP plans, and CCP controls',
    'Verify GFSI scheme and regulatory compliance obligations',
    'Support continual food safety improvement and certification readiness'
  ];

  const targetAudience = [
    'Food Safety Managers and FSMS Coordinators',
    'HACCP Team Leaders and Food Safety Officers',
    'Internal and External Food Safety Auditors',
    'GFSI Scheme Auditors (FSSC 22000, BRC, SQF)',
    'Quality Assurance Professionals in Food Industry',
    'Food Safety Consultants and Advisors',
    'Graduates aspiring to become food safety lead auditors'
  ];

  const professionalBenefits = [
    {
      title: 'Globally recognized food safety lead auditor qualification',
      icon: Award
    },
    {
      title: 'Career advancement in food safety auditing, consulting, and compliance',
      icon: TrendingUp
    },
    {
      title: 'Enhanced credibility as an ISO 22000 and GFSI-recognized expert',
      icon: Shield
    }
  ];

  const organizationalBenefits = [
    {
      title: 'Ability to lead internal, supplier, and certification FSMS audits',
      icon: Users
    },
    {
      title: 'Stronger HACCP system effectiveness and food safety performance',
      icon: Target
    },
    {
      title: 'Reduced food safety incidents, recalls, and consumer complaints',
      icon: CheckCircle
    }
  ];

  const businessBenefits = [
    {
      title: 'Supports GFSI scheme certification readiness and maintenance',
      icon: Award
    },
    {
      title: 'Enhances consumer trust and regulatory confidence',
      icon: Shield
    },
    {
      title: 'Meets customer, retailer, and regulatory food safety requirements',
      icon: CheckCircle
    }
  ];

  const careerOpportunities = [
    'ISO 22000 Lead Auditor',
    'FSMS Consultant',
    'GFSI Scheme Auditor',
    'Food Safety Manager',
    'Third-party Certification Auditor',
    'HACCP Team Leader'
  ];

  const industries = [
    'Food Manufacturing',
    'Beverage Industry',
    'Food Service & Hospitality',
    'Retail & Supermarkets',
    'Agricultural & Primary Production',
    'Food Packaging',
    'Logistics & Cold Chain'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="ISO 22000 Lead Auditor Course | Food Safety Management System Certification | ISDE Academy"
        description="Achieve ISO 22000 Lead Auditor certification with ISDE Academy. 5-day expert-led FSMS lead auditor course covering HACCP audit, PRP verification, food chain compliance, and GFSI scheme readiness. Advance your food safety career globally."
        keywords="ISO 22000 lead auditor, food safety lead auditor certification, FSMS lead auditor course, HACCP lead auditor, GFSI auditor training, food safety management system certification, ISO 22000 auditor India, food safety auditor course"
        canonical="/ISO-22000-Lead-Auditor-Course"
        structuredData={structuredData}
        breadcrumbs={[
          { name: 'Training', path: '/training' },
          { name: 'ISO Training Services', path: '/training/iso-training-services' },
          { name: 'ISO 22000 Lead Auditor', path: '/ISO-22000-Lead-Auditor-Course' }
        ]}
      />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-600 to-amber-600 text-white py-20 mt-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full text-sm mb-6 font-semibold">
              <Shield className="h-5 w-5" />
              FOOD SAFETY MANAGEMENT SYSTEMS
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              ISO 22000 Lead Auditor Course
            </h1>
            <p className="text-xl md:text-2xl text-orange-100 mb-8 max-w-4xl mx-auto leading-relaxed">
              Become a globally recognized ISO 22000 Lead Auditor. Master food safety management system audits, HACCP verification, and GFSI scheme compliance to protect consumers and advance your career.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-lg">
              <div className="flex items-center gap-2">
                <Calendar className="h-6 w-6" />
                <span>5 Days Training</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-6 w-6" />
                <span>Professional Certificate</span>
              </div>
              <div className="flex items-center gap-2">
                <BookOpen className="h-6 w-6" />
                <span>Global Recognition</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Back Button */}
      <section className="bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <Button
            onClick={() => navigate('/training/iso-training-services')}
            variant="outline"
            className="flex items-center gap-2 hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to ISO Training Services
          </Button>
        </div>
      </section>

      {/* ISO Progression Path */}
      <ISOProgressionPath standard={standards['iso-22000']} currentLevel="leadAuditor" />

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              The ISO 22000 Lead Auditor Course is a globally recognized professional training programme designed to develop competent food safety auditors capable of planning, leading, and managing audits of Food Safety Management Systems (FSMS) in accordance with ISO 22000:2018. As food safety regulations tighten, GFSI scheme certifications become standard requirements for market access, and consumer expectations rise, the demand for qualified ISO 22000 lead auditors continues to grow across the global food chain.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              This 5-day intensive course equips food safety professionals with advanced FSMS auditing skills, comprehensive knowledge of ISO 22000 requirements and HACCP principles, and internationally recognized certification credentials to lead audits across the food manufacturing, food service, and food logistics sectors.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is ISO 22000 Lead Auditor Course Section */}
      <section className="py-16 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What Is the ISO 22000 Lead Auditor Course?
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
              The ISO 22000 Lead Auditor Course provides comprehensive training in conducting first-party, second-party, and third-party audits of Food Safety Management Systems based on ISO 22000:2018 standards. Following ISO 19011 auditing guidelines, participants learn to plan, conduct, report, and follow up FSMS audits — including HACCP plan verification, prerequisite program assessment, hazard analysis audit, and traceability system review.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card className="h-full border-2 border-orange-200 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    Certified participants gain competence to:
                  </h3>
                  <div className="space-y-3">
                    {courseCompetencies.map((competency, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{competency}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="flex items-center justify-center"
            >
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-orange-500 to-amber-600 shadow-2xl mb-4">
                  <Award className="h-16 w-16 text-white" />
                </div>
                <p className="text-lg font-semibold text-gray-800">
                  Certified Food Safety<br />Lead Auditor
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who Should Attend Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Who Should Attend the ISO 22000 Lead Auditor Course?
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              This course is ideal for food safety and quality professionals seeking advanced auditor credentials, including:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {targetAudience.map((role, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <Card className="h-full border-l-4 border-orange-500 hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-orange-600 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-800">{role}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Benefits of ISO 22000 Lead Auditor Course
            </h3>
          </motion.div>

          {/* Professional Benefits */}
          <div className="mb-12">
            <h4 className="text-2xl font-bold text-gray-900 mb-6 text-center">Professional Benefits</h4>
            <div className="grid md:grid-cols-3 gap-6">
              {professionalBenefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="h-full bg-white hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-orange-100 to-amber-200 mb-4">
                        <benefit.icon className="h-8 w-8 text-orange-700" />
                      </div>
                      <p className="text-gray-800 font-medium">{benefit.title}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Organizational Benefits */}
          <div className="mb-12">
            <h4 className="text-2xl font-bold text-gray-900 mb-6 text-center">Organizational Benefits</h4>
            <div className="grid md:grid-cols-3 gap-6">
              {organizationalBenefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="h-full bg-white hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-orange-100 to-amber-200 mb-4">
                        <benefit.icon className="h-8 w-8 text-orange-700" />
                      </div>
                      <p className="text-gray-800 font-medium">{benefit.title}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Business & Market Benefits */}
          <div>
            <h4 className="text-2xl font-bold text-gray-900 mb-6 text-center">Business & Market Benefits</h4>
            <div className="grid md:grid-cols-3 gap-6">
              {businessBenefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="h-full bg-white hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-orange-100 to-amber-200 mb-4">
                        <benefit.icon className="h-8 w-8 text-orange-700" />
                      </div>
                      <p className="text-gray-800 font-medium">{benefit.title}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Career Opportunities Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Career Opportunities After ISO 22000 Lead Auditor Course
            </h4>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Earning ISO 22000 Lead Auditor certification opens doors to high-demand career roles, including:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
            {careerOpportunities.map((career, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <Card className="h-full bg-gradient-to-br from-orange-50 to-white border-2 border-orange-200 hover:shadow-lg transition-all">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-600 flex items-center justify-center">
                      <Award className="h-5 w-5 text-white" />
                    </div>
                    <span className="font-semibold text-gray-900">{career}</span>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Industries Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-orange-600 to-amber-600 rounded-2xl p-8 text-white"
          >
            <h5 className="text-2xl font-bold mb-4 text-center">
              Industries Hiring ISO 22000 Lead Auditors
            </h5>
            <div className="flex flex-wrap justify-center gap-4">
              {industries.map((industry, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full font-medium"
                >
                  {industry}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Courses */}
      <RelatedCoursesSection currentSlug="iso-22000" />

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-orange-600 to-amber-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Become an ISO 22000 Lead Auditor?
            </h2>
            <p className="text-xl text-orange-100 mb-8">
              Join food safety professionals worldwide who have advanced their careers with our comprehensive ISO 22000 Lead Auditor certification
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/contact')}
                className="bg-white text-orange-700 hover:bg-orange-50 font-semibold px-8 py-6 text-lg"
              >
                Enroll Now
              </Button>
              <Button
                onClick={() => navigate('/training-calendar')}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-orange-700 font-semibold px-8 py-6 text-lg"
              >
                View Schedule
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
